import {useEffect, useMemo, useState} from "react";
import {useIntegrator} from "../../integrator.context";
import {get} from "../../utils/http.client";
import {apiBindings} from "../../utils/constants";
import {RemoteConstantsModel} from "../../models/remoteConstants.model";
import {BranchDiff, diffBranches} from "../../utils/branchDiff";
import SectionDiffView from "./sectionDiffView";
import SelectableLabel from "../../elements/selectable.label";

interface DiffViewProps {
    onClose(): void;
}

// Persist the last "Other" choice keyed by the Current branch, so reopening
// the dialog while still on the same branch lands on the same comparison
// you were last using. Switching to a different Current branch resets to
// the default. Stored values that are no longer valid (branch deleted,
// renamed, etc.) gracefully fall back to the first candidate.
const STORAGE_KEY_PREFIX = 'rift-diff-other-';

function loadRememberedOther(currentBranch: string, candidates: string[]): string {
    if (typeof window === 'undefined') return candidates[0] ?? '';
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX + currentBranch);
    if (stored && candidates.includes(stored)) return stored;
    return candidates[0] ?? '';
}

function DiffView(props: DiffViewProps) {
    const {branch, branches, getBranchVersionNumber, state} = useIntegrator();

    const otherCandidates = branches.map(b => b.name).filter(n => n !== branch);
    const [target, setTarget] = useState<string>(() => loadRememberedOther(branch, otherCandidates));

    function chooseTarget(next: string) {
        setTarget(next);
        if (typeof window !== 'undefined') {
            if (next) localStorage.setItem(STORAGE_KEY_PREFIX + branch, next);
            else localStorage.removeItem(STORAGE_KEY_PREFIX + branch);
        }
    }
    const [targetData, setTargetData] = useState<RemoteConstantsModel | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (target === '') {
            setTargetData(undefined);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(undefined);
            const result = await get(`${apiBindings.api}getRemoteConstants?branch=${target}`, undefined);
            if (cancelled) return;
            if (!result.success) {
                setError(`Couldn't fetch '${target}'.`);
                setTargetData(undefined);
            } else {
                setTargetData(result.data as RemoteConstantsModel);
            }
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [target]);

    const diff: BranchDiff | undefined = useMemo(() => {
        if (!targetData) return undefined;
        return diffBranches(state, targetData);
    }, [state, targetData]);

    return (
        <div className={'layout vertical overlay'} onClick={props.onClose}>
            <div className={'layout vertical gap box'}
                 style={{padding: 15, gap: 10, width: '80vw', height: '85vh'}}
                 onClick={e => e.stopPropagation()}>
                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    <p className={'text header'}>Compare Branches</p>
                    <button onClick={props.onClose}>
                        <span className={'fa-solid fa-xmark'}/>
                    </button>
                </div>
                <div className={'layout horizontal center gap'} style={{gap: 15}}>
                    <p>
                        <strong>Current:</strong> {branch} v{getBranchVersionNumber(branch)}
                    </p>
                    <span className={'fa-solid fa-arrow-right'}/>
                    <p><strong>Other:</strong></p>
                    <SelectableLabel name={''} value={target} options={otherCandidates} onChange={v => chooseTarget(String(v))}/>
                    {target !== '' && <p style={{color: 'rgba(255,255,255,0.66)'}}>v{getBranchVersionNumber(target)}</p>}
                </div>
                <div className={'layout vertical gap'} style={{flexGrow: 1, overflowY: 'auto', gap: 6}}>
                    {loading && <p>Loading…</p>}
                    {error && <p style={{color: '#ff6666'}}>{error}</p>}
                    {!loading && !error && diff && targetData && diff.sections.map(section => (
                        <SectionDiffView key={section.title} section={section} targetData={targetData}/>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default DiffView;
